import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  CalendarDots, GameController, Trophy, Users, MapPin,
  Lightning, ArrowRight, BookOpen, Bell, Heart,
  ChatCircle, ChartBar, Plus, Sparkle, Star, Compass,
  Shield, Skull, FilePdf,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { NotificationBell } from '../components/NotificationBell';
import { Logo } from '../components/Logo';
import { ActivationChecklist } from './OnboardingWizard';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myEvents, setMyEvents] = useState([]);
  const [myTournaments, setMyTournaments] = useState([]);
  const [stats, setStats] = useState(null);
  const [recs, setRecs] = useState([]);
  const [beginnerPicks, setBeginnerPicks] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [checklistInfo, setChecklistInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [myEventsRes, tournsRes, statsRes, recsRes, beginnerRes, nearbyRes, checklistRes] = await Promise.all([
          ax().get('/api/sessions/my').catch(() => ({ data: [] })),
          ax().get('/api/tournaments').catch(() => ({ data: [] })),
          ax().get(`/api/users/${user?.id}/stats`).catch(() => ({ data: null })),
          ax().get('/api/recommendations/games?limit=8').catch(() => ({ data: [] })),
          ax().get('/api/recommendations/beginner-friendly?limit=5').catch(() => ({ data: [] })),
          ax().get('/api/discover/feed?feed_type=sessions&limit=10').catch(() => ({ data: [] })),
          ax().get('/api/onboarding/activation-tasks').catch(() => ({ data: null })),
        ]);
        // Filter to upcoming/open sessions
        const myList = Array.isArray(myEventsRes.data) ? myEventsRes.data : [];
        const upcoming = myList.filter(e => e.status === 'open' || e.status === 'upcoming');
        setMyEvents(upcoming.slice(0, 5));
        const tournsList = Array.isArray(tournsRes.data) ? tournsRes.data : [];
        setMyTournaments(tournsList.filter(t => t.status !== 'completed').slice(0, 4));
        setStats(statsRes.data);
        setRecs(Array.isArray(recsRes.data) ? recsRes.data : []);
        setBeginnerPicks(Array.isArray(beginnerRes.data) ? beginnerRes.data : []);
        const nearbyList = Array.isArray(nearbyRes.data) ? nearbyRes.data.filter(e => e.host_id !== user?.id) : [];
        setNearby(nearbyList.slice(0, 3));
        setChecklistInfo(checklistRes.data);
      } catch {}
      setLoading(false);
    };
    if (user?.id) load();
  }, [user?.id]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const SHORTCUTS = [
    { label: 'New Session', icon: Plus, color: '#9D4CDD', path: '/create-event', primary: true },
    { label: 'New Tournament', icon: Trophy, color: '#F59E0B', path: '/create-tournament', primary: true },
    { label: 'Messages', icon: ChatCircle, color: '#EC4899', path: '/messages' },
    { label: 'Rules', icon: BookOpen, color: '#3B82F6', path: '/rules' },
  ];

  return (
    <div className="app-container" data-testid="home-page">
      {/* Branded top strip */}
      <div className="px-6 pt-3 pb-1 flex items-center justify-between">
        <Logo variant="wordmark" size="xs" clickable={true} />
        <NotificationBell />
      </div>

      {/* Header */}
      <header className="px-6 pt-4 pb-2">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[#71717A] text-xs">{greeting()}</p>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              {user?.name || 'Player'}
            </h1>
          </div>
        </div>
      </header>

      <div className="px-6 pb-36 space-y-5">
        {/* Welcome Back hero — surfaces 3 contextual CTAs:
            (1) progress on activation checklist, (2) nearby open seat, (3) a smart rec. */}
        <WelcomeBackHero
          user={user}
          checklistInfo={checklistInfo}
          nearbyEvent={nearby[0]}
          topRec={recs[0]}
          onNavigate={navigate}
        />

        {/* Activation Checklist */}
        <ActivationChecklist />

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-2" data-testid="quick-stats">
            <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-white">{stats.total_games || 0}</p>
              <p className="text-[9px] text-[#555] uppercase tracking-wider">Games</p>
            </div>
            <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-[#39FF14]">{stats.wins || 0}</p>
              <p className="text-[9px] text-[#555] uppercase tracking-wider">Wins</p>
            </div>
            <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-[#9D4CDD]">Lv.{stats.level || 0}</p>
              <p className="text-[9px] text-[#555] uppercase tracking-wider">Level</p>
            </div>
          </div>
        )}

        {/* Quick Shortcuts */}
        <div>
          <h3 className="text-white text-sm font-semibold mb-3">Quick Access</h3>
          <div className="grid grid-cols-4 gap-2">
            {SHORTCUTS.map(s => (
              <button key={s.label} onClick={() => navigate(s.path)}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-[#0e0e16] border border-white/[0.06] hover:border-white/[0.12] transition-colors"
                data-testid={`shortcut-${s.label.toLowerCase()}`}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}12` }}>
                  <s.icon size={18} weight="duotone" style={{ color: s.color }} />
                </div>
                <span className="text-[10px] text-[#888]">{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tabletop Toolkit — character sheets, wargame rosters, and (for
            admins/publishers) rulebook PDF uploads. Kept in a distinct
            section so it doesn't crowd the primary session/tournament CTAs. */}
        <div data-testid="tabletop-toolkit">
          <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-[#F59E0B]" />
            Tabletop Toolkit
          </h3>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => navigate('/character-sheets')} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-[#0e0e16] border border-white/[0.06] hover:border-[#F59E0B]/30 transition-colors" data-testid="shortcut-character-sheets">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#F59E0B]/10">
                <Shield size={18} weight="duotone" className="text-[#F59E0B]" />
              </div>
              <span className="text-[10px] text-[#888]">D&D Sheets</span>
            </button>
            <button onClick={() => navigate('/wargame-rosters')} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-[#0e0e16] border border-white/[0.06] hover:border-[#DC2626]/30 transition-colors" data-testid="shortcut-wargame-rosters">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#DC2626]/10">
                <Skull size={18} weight="duotone" className="text-[#DC2626]" />
              </div>
              <span className="text-[10px] text-[#888]">Warhammer</span>
            </button>
            {(user?.role === 'admin' || user?.role === 'publisher') ? (
              <button onClick={() => navigate('/game-pdfs')} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-[#0e0e16] border border-white/[0.06] hover:border-[#EF4444]/30 transition-colors" data-testid="shortcut-game-pdfs">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#EF4444]/10">
                  <FilePdf size={18} weight="duotone" className="text-[#EF4444]" />
                </div>
                <span className="text-[10px] text-[#888]">Rulebook PDFs</span>
              </button>
            ) : (
              <button onClick={() => navigate('/ai')} className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-[#0e0e16] border border-white/[0.06] hover:border-[#00F0FF]/30 transition-colors" data-testid="shortcut-ai-assistant">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#00F0FF]/10">
                  <ChatCircle size={18} weight="duotone" className="text-[#00F0FF]" />
                </div>
                <span className="text-[10px] text-[#888]">Ask AI</span>
              </button>
            )}
          </div>
        </div>

        {/* Upcoming Sessions */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-semibold flex items-center gap-2">
              <CalendarDots size={16} weight="duotone" className="text-[#00F0FF]" /> My Events
            </h3>
            <button onClick={() => navigate('/create-event')} className="flex items-center gap-1 text-[#9D4CDD] text-xs hover:underline" data-testid="my-events-create-btn">
              <Plus size={12} weight="bold" /> Create
            </button>
          </div>
          {loading ? (
            <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-20 bg-white/[0.03] rounded-xl animate-pulse" />)}</div>
          ) : myEvents.length > 0 ? (
            <div className="space-y-2">
              {myEvents.map(evt => (
                <button key={evt.id} onClick={() => navigate(`/events/${evt.id}`)}
                  className="w-full bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3.5 text-left hover:border-white/[0.12] transition-colors flex items-center gap-3"
                  data-testid={`event-${evt.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center shrink-0">
                    <GameController size={18} weight="duotone" className="text-[#00F0FF]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{evt.title || evt.game_type}</p>
                    <p className="text-[#555] text-[10px] mt-0.5">
                      {evt.date} · {evt.time} · {evt.current_players}/{evt.max_players} players
                    </p>
                  </div>
                  <ArrowRight size={14} className="text-[#333]" />
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-6 text-center">
              <CalendarDots size={28} weight="duotone" className="text-[#333] mx-auto mb-2" />
              <p className="text-[#555] text-xs mb-3">No game sessions yet</p>
              <button onClick={() => navigate('/create-event')} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#9D4CDD] text-white text-xs font-semibold hover:bg-[#7C3AED] transition-colors" data-testid="empty-create-session-btn">
                <Plus size={14} weight="bold" /> Create Your First Session
              </button>
            </div>
          )}
        </div>

        {/* Active Tournaments — always shown with create CTA */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-semibold flex items-center gap-2">
              <Trophy size={16} weight="duotone" className="text-[#F59E0B]" /> Active Tournaments
            </h3>
            <button onClick={() => navigate('/create-tournament')} className="flex items-center gap-1 text-[#F59E0B] text-xs hover:underline" data-testid="my-tournaments-create-btn">
              <Plus size={12} weight="bold" /> Create
            </button>
          </div>
          {myTournaments.length > 0 ? (
            <div className="space-y-2">
              {myTournaments.map(t => (
                <button key={t.id} onClick={() => navigate(`/tournaments/${t.id}`)}
                  className="w-full bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3.5 text-left hover:border-white/[0.12] transition-colors flex items-center gap-3"
                  data-testid={`tournament-${t.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center shrink-0">
                    <Trophy size={18} weight="duotone" className="text-[#F59E0B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{t.name}</p>
                    <p className="text-[#555] text-[10px] mt-0.5">{t.game_type} · {t.current_players}/{t.max_players} · {t.status}</p>
                  </div>
                  <ArrowRight size={14} className="text-[#333]" />
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-6 text-center">
              <Trophy size={28} weight="duotone" className="text-[#333] mx-auto mb-2" />
              <p className="text-[#555] text-xs mb-3">No active tournaments</p>
              <button onClick={() => navigate('/create-tournament')} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F59E0B] text-black text-xs font-semibold hover:bg-[#F59E0B]/90 transition-colors" data-testid="empty-create-tournament-btn">
                <Plus size={14} weight="bold" /> Host a Tournament
              </button>
            </div>
          )}
        </div>
        {/* Discover New Games — personalized recommendations */}
        {recs.length > 0 && (
          <div data-testid="recommendations-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white text-sm font-semibold flex items-center gap-2">
                <Compass size={16} weight="duotone" className="text-[#9D4CDD]" /> Discover New Games
              </h3>
              <button onClick={() => navigate('/collection')} className="text-[#9D4CDD] text-xs hover:underline" data-testid="browse-all-games-btn">
                Browse all →
              </button>
            </div>
            <div className="flex gap-2 overflow-x-auto -mx-6 px-6 pb-2 scrollbar-none">
              {recs.map(r => (
                <button
                  key={r.slug}
                  onClick={() => navigate(`/rules/${r.slug}`)}
                  className="min-w-[180px] bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3 text-left shrink-0 hover:border-[#9D4CDD]/30 transition-colors"
                  data-testid={`rec-${r.slug}`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Sparkle size={12} weight="fill" className="text-[#9D4CDD]" />
                    <span className="text-[9px] uppercase tracking-wider font-semibold text-[#9D4CDD]">For you</span>
                  </div>
                  <p className="text-white text-sm font-medium truncate">{r.name}</p>
                  <p className="text-[10px] text-[#555] mt-1 line-clamp-2 min-h-[28px]">
                    {(r.reasons && r.reasons[0]) || r.publisher || ''}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-[9px] text-[#555]">
                    {r.min_players ? <span>{r.min_players}–{r.max_players}p</span> : null}
                    {r.complexity ? <span className="capitalize">· {r.complexity}</span> : null}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Best for First-Timers — community-curated beginner-friendly picks.
            Pulls games where modal beginner_friendly is "very_friendly" + ≥3
            ratings; falls back to easy/light catalog games when data is thin. */}
        {beginnerPicks.length > 0 && (
          <div data-testid="beginner-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white text-sm font-semibold flex items-center gap-2">
                <Heart size={16} weight="duotone" className="text-[#39FF14]" /> Best for First-Timers
              </h3>
              <span className="text-[10px] text-[#555] uppercase tracking-wider">Community picks</span>
            </div>
            <div className="flex gap-2 overflow-x-auto -mx-6 px-6 pb-2 scrollbar-none">
              {beginnerPicks.map(g => (
                <button
                  key={g.slug}
                  onClick={() => navigate(`/rules/${g.slug}`)}
                  className="min-w-[180px] bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3 text-left shrink-0 hover:border-[#39FF14]/30 transition-colors"
                  data-testid={`beginner-${g.slug}`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Heart size={11} weight="fill" className="text-[#39FF14]" />
                    <span className="text-[9px] uppercase tracking-wider font-semibold text-[#39FF14]">Beginner</span>
                  </div>
                  <p className="text-white text-sm font-medium truncate">{g.name}</p>
                  <p className="text-[10px] text-[#555] mt-1 min-h-[28px] line-clamp-2">
                    {g.is_fallback
                      ? `Light complexity · ${g.min_players || '?'}-${g.max_players || '?'} players`
                      : `${g.very_friendly_pct}% rate it very friendly · ${g.fun_avg?.toFixed(1)}★ fun`}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-[9px] text-[#555]">
                    {g.complexity ? <span className="capitalize">· {g.complexity}</span> : null}
                    {!g.is_fallback && g.ratings_count ? <span>· {g.ratings_count} rated</span> : null}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
//  Welcome Back hero — 3 contextual CTAs based on what the user
//  needs *right now*. Smart defaults so it never looks empty.
// ───────────────────────────────────────────────────────────────────────
function WelcomeBackHero({ user, checklistInfo, nearbyEvent, topRec, onNavigate }) {
  const cards = [];

  // CTA 1 — Activation Checklist progress (if not 100%)
  if (checklistInfo && checklistInfo.progress_pct < 100) {
    cards.push({
      key: 'quests',
      icon: Star,
      color: '#F59E0B',
      label: 'XP Quests',
      title: `${checklistInfo.completed}/${checklistInfo.total} done`,
      sub: `Earn more XP — ${100 - (checklistInfo.progress_pct || 0)}% left`,
      onClick: () => {
        const el = document.querySelector('[data-testid="activation-checklist"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    });
  }

  // CTA 2 — Nearby open seat (only if there's an actual event with seats)
  if (nearbyEvent && (nearbyEvent.open_slots ?? 1) > 0) {
    cards.push({
      key: 'nearby',
      icon: MapPin,
      color: '#00F0FF',
      label: 'Open Seat',
      title: nearbyEvent.title || nearbyEvent.game || 'Game tonight',
      sub: `${nearbyEvent.location || 'Nearby'} · ${nearbyEvent.open_slots ?? '?'} spot${nearbyEvent.open_slots === 1 ? '' : 's'} left`,
      onClick: () => onNavigate(`/events/${nearbyEvent.id}`),
    });
  }

  // CTA 3 — Top game recommendation
  if (topRec) {
    cards.push({
      key: 'rec',
      icon: Sparkle,
      color: '#9D4CDD',
      label: 'For you',
      title: topRec.name,
      sub: (topRec.reasons && topRec.reasons[0]) || 'New game pick',
      onClick: () => onNavigate(`/rules/${topRec.slug}`),
    });
  }

  // Always at least one fallback so the hero isn't empty
  if (cards.length === 0) {
    cards.push({
      key: 'create',
      icon: Plus,
      color: '#9D4CDD',
      label: 'Get started',
      title: 'Host your first session',
      sub: 'Pick a game, invite friends, earn XP',
      onClick: () => onNavigate('/create-event'),
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#9D4CDD]/12 via-[#0e0e16] to-[#00F0FF]/8 border border-white/[0.06] rounded-2xl p-4"
      data-testid="welcome-back-hero"
    >
      <div className="flex items-center gap-2 mb-3">
        <Lightning size={14} weight="fill" className="text-[#F59E0B]" />
        <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#9D4CDD]">
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {cards.slice(0, 3).map(c => (
          <button
            key={c.key}
            onClick={c.onClick}
            className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/[0.04] hover:border-white/[0.12] hover:bg-white/[0.06] transition-all text-left"
            data-testid={`hero-cta-${c.key}`}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${c.color}1f` }}
            >
              <c.icon size={18} weight="duotone" style={{ color: c.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: c.color }}>
                {c.label}
              </p>
              <p className="text-white text-sm font-medium truncate">{c.title}</p>
              <p className="text-[#666] text-[10px] truncate">{c.sub}</p>
            </div>
            <ArrowRight size={14} className="text-[#333] shrink-0" />
          </button>
        ))}
      </div>
    </motion.div>
  );
}
